<template>
    <Modal
        :show="isInMaintenance"
        :closeOnBackdropClick="false"
        size="small"
        title="TaxiFusion Wartung"
    >
        <div class="maintenance-modal">
            <!-- Header -->
            <div class="maintenance-header">
                <p class="maintenance-subtitle">{{ maintenanceMessage }}</p>
            </div>

            <!-- Status Content -->
            <div class="maintenance-status-content">
                <!-- Countdown Timer -->
                <div v-if="timeUntilEnd" class="maintenance-timer-section">
                    <div class="timer-header">
                        <i class="ri-time-line timer-icon"></i>
                        <span class="timer-label">Wir sind wieder online in:</span>
                    </div>
                    <div class="countdown-display">
                        <div class="countdown">{{ formatTime(timeUntilEnd) }}</div>
                        <div class="countdown-progress">
                            <div
                                class="progress-bar"
                                :style="{ width: progressPercentage + '%' }"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Checking Status -->
                <div
                    v-else-if="isInMaintenance && !timeUntilEnd"
                    class="maintenance-checking-section"
                >
                    <div class="checking-content">
                        <i class="ri-loader-4-line spinning-icon"></i>
                        <p class="checking-text">Wartung läuft - Überprüfe Status...</p>
                    </div>
                </div>

                <!-- Maintenance Window Info -->
                <div v-if="maintenanceWindow" class="maintenance-window-section">
                    <div class="window-header">
                        <i class="ri-calendar-line window-icon"></i>
                        <span class="window-label">Geplante Wartungszeit:</span>
                    </div>
                    <div class="window-times">
                        <div class="time-item">
                            <span class="time-label">Von:</span>
                            <span class="time-value">{{
                                formatDateTime(maintenanceWindow.from)
                            }}</span>
                        </div>
                        <div class="time-item">
                            <span class="time-label">Bis:</span>
                            <span class="time-value">{{
                                formatDateTime(maintenanceWindow.to)
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import { maintenanceService } from '@/services/axiosConfig';
import Modal from '@/components/widgets/Modal.vue';

export default {
    name: 'MaintenanceHandler',
    components: {
        Modal,
    },
    data() {
        return {
            isInMaintenance: false,
            maintenanceMessage: '',
            maintenanceWindow: null,
            timeUntilEnd: null,
            checkInterval: null,
        };
    },
    computed: {
        progressPercentage() {
            if (!this.maintenanceWindow || !this.timeUntilEnd) return 0;

            const totalDuration = this.maintenanceWindow.to - this.maintenanceWindow.from;
            const remainingTime = this.timeUntilEnd;
            const elapsedTime = totalDuration - remainingTime;

            return Math.min(100, Math.max(0, (elapsedTime / totalDuration) * 100));
        },
    },
    mounted() {
        this.checkMaintenanceStatus();
        this.startPeriodicCheck();
    },
    beforeDestroy() {
        this.stopPeriodicCheck();
    },
    methods: {
        checkMaintenanceStatus() {
            const status = maintenanceService.getMaintenanceStatus();
            this.isInMaintenance = status.isInMaintenance;
            this.maintenanceMessage = status.message;
            this.maintenanceWindow = status.maintenanceWindow;

            if (this.isInMaintenance) {
                this.updateTimeUntilEnd();
            } else {
                this.timeUntilEnd = null;
            }
        },

        updateTimeUntilEnd() {
            const timeUntilEnd = maintenanceService.getTimeUntilMaintenanceEnd();

            if (!timeUntilEnd || timeUntilEnd <= 0) {
                this.timeUntilEnd = null;
                this.checkIfMaintenanceFinished();
            } else {
                this.timeUntilEnd = timeUntilEnd;
            }
        },

        async checkIfMaintenanceFinished() {
            try {
                const isFinished = await maintenanceService.checkMaintenanceFinished();
                if (isFinished) {
                    this.checkMaintenanceStatus();
                }
            } catch (error) {
            }
        },

        startPeriodicCheck() {
            this.checkInterval = setInterval(() => {
                this.checkMaintenanceStatus();
                if (this.isInMaintenance) {
                    this.updateTimeUntilEnd();
                }
            }, 1000);
        },

        stopPeriodicCheck() {
            if (this.checkInterval) {
                clearInterval(this.checkInterval);
                this.checkInterval = null;
            }
        },

        formatTime(milliseconds) {
            if (!milliseconds) return '--:--:--';

            const totalSeconds = Math.floor(milliseconds / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            return `${hours.toString().padStart(2, '0')}:${minutes
                .toString()
                .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },

        formatDateTime(date) {
            if (!date) return '';

            const targetDate = new Date(date);
            const today = new Date();

            const isSameDay = targetDate.toDateString() === today.toDateString();

            if (isSameDay) {
                const timeString = targetDate.toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                return timeString + ' Uhr';
            } else {
                return targetDate.toLocaleString('de-DE', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
        },
    },
};
</script>

<style scoped>
.maintenance-modal {
    padding: 0;
    margin: 0 auto;

    .maintenance-icon-container {
        margin-bottom: 16px;
    }

    .maintenance-main-icon {
        font-size: 48px;
        opacity: 0.9;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    }
    .maintenance-title {
        font-size: 28px;
        font-weight: 700;
        margin: 0 0 8px 0;
        padding: 0;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    .maintenance-subtitle {
        font-size: 16px;
        opacity: 0.9;
        margin-bottom: 20px;
        margin: 0;
        font-weight: 400;
    }

    /* Timer Section */
    .maintenance-timer-section {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 12px;
        padding: 24px;
        margin-top: 20px;
        margin-bottom: 20px;
        border: 0.5px solid #e9ecef;
    }

    .timer-header {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-bottom: 16px;
    }

    .timer-icon {
        font-size: 20px;
        color: #953701;
    }

    .timer-label {
        font-weight: 600;
        color: #495057;
        font-size: 16px;
    }

    .countdown-display {
        text-align: center;
    }

    .countdown {
        font-size: 36px;
        font-weight: 700;
        color: #953701;
        font-family: 'SF Mono', 'Monaco', 'Cascadia Code', monospace;
        margin-bottom: 12px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        letter-spacing: 2px;
    }

    .countdown-progress {
        width: 100%;
        height: 4px;
        background: #e9ecef;
        border-radius: 2px;
        overflow: hidden;
    }

    .progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #953701 0%, #c44e1e 100%);
        border-radius: 2px;
        transition: width 0.3s ease;
    }

    /* Checking Status Section */
    .maintenance-checking-section {
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        border: 0.5px solid #ffeaa7;
        border-radius: 12px;
        padding: 24px;
        margin-bottom: 20px;
    }

    .checking-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
    }

    .spinning-icon {
        font-size: 32px;
        color: #856404;
        animation: spin 1s linear infinite;
    }

    .checking-text {
        font-weight: 600;
        color: #856404;
        margin: 0;
        text-align: center;
    }

    /* Window Section */
    .maintenance-window-section {
        background: linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%);
        border: 0.5px solid #bee5eb;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 20px;
    }

    .window-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
    }

    .window-icon {
        font-size: 18px;
        color: #0c5460;
    }

    .window-label {
        font-weight: 600;
        color: #0c5460;
        font-size: 15px;
    }

    .window-times {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .time-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 6px;
    }

    .time-label {
        font-weight: 600;
        color: #495057;
        font-size: 14px;
    }

    .time-value {
        font-weight: 700;
        color: #0c5460;
        font-size: 14px;
    }

    /* Footer */
    .maintenance-footer {
        background: #f8f9fa;
        padding: 20px 24px;
        border-radius: 0 0 12px 12px;
        border-top: 0.5px solid #e9ecef;
    }

    .footer-message {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 14px;
        color: #6c757d;
        text-align: center;
        justify-content: center;
    }

    .footer-message i {
        font-size: 16px;
        color: #953701;
    }

    /* Animations */
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    /* Responsive */
    @media (max-width: 480px) {
        .maintenance-header {
            padding: 24px 16px;
        }

        .maintenance-main-icon {
            font-size: 40px;
        }

        .maintenance-title {
            font-size: 24px;
        }

        .maintenance-status-content {
            padding: 16px;
        }

        .countdown {
            font-size: 32px;
        }
    }
}
</style>
